import React, { useState, useEffect } from "react";
import editIcon from "/icons/edit.png";
import deleteIcon from "/icons/delete.png";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmation";
import VaccinationForm from "../petForms/VaccinationForm";
import MedicationForm from "../petForms/MedicationForm";
import PastIllnessForm from "../petForms/PastIllnessForm";
import AllergyForm from "../petForms/AllergyForm";
import SurgicalHistoryForm from "../petForms/SurgicalHistoryForm";
import DentalRecordsForm from "../petForms/DentalRecordsForm";
import DewormingAntiFleaForm from "../petForms/DewormingAntiFleaForm";
import SOAPDetailsModal from "../modals/SOAPDetailsModal";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import { useTranslation } from "react-i18next";

const MedicalRecordView = ({
  title,
  icon,
  columns,
  fetchUrl,
  formType,
  petData,
  showActions = true, // New prop to control action buttons visibility
  medicalHistoryData = null, // New prop for shared medical history data
  useSharedData = false, // New prop to indicate if we should use shared data
  onMedicalHistoryChange = null, // Callback to refresh medical history summary
}) => {
  const { getUserType } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n?.dir?.() === "rtl";

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  const petLabel = petData
    ? `${petData.species} / ${petData.breed} – ${petData.age}`
    : "";
  const MIN_VISIBLE_ROWS = 5;
  // Use MIN_VISIBLE_ROWS as the page size so pagination only appears when there's more than the
  // minimum visible rows (previously pagination kicked in at 2 items which caused the UI to
  // show placeholders but still paginate). This makes the table show up to 5 records per page.
  const rowsPerPage = MIN_VISIBLE_ROWS;

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // add | edit
  const [formData, setFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [row, setRow] = useState();
  const [index, setIndex] = useState();
  const [showSOAPModal, setShowSOAPModal] = useState(false);
  const [selectedSOAPRow, setSelectedSOAPRow] = useState(null);

  // helper
  const getId = (row) => row?.id ?? row?.ID ?? row?.Id ?? row?._id ?? null;
  const cap = (s) =>
    typeof s === "string" && s.length ? s[0].toUpperCase() + s.slice(1) : s;

  const normalizeRowForDisplay = (row) => {
    const id = getId(row); // <-- capture id

    // Vaccination
    if ("vaccine_name" in row || "Vaccine Name" in row) {
      return {
        ...row,
        id, // <-- keep id
        Veterinarian: row.Veterinarian ?? row.veterinarian,
        "Vaccine Name": row["Vaccine Name"] ?? row.vaccine_name,
        "Date Administered": row["Date Administered"] ?? row.date_administered,
        "Next Due Date": row["Next Due Date"] ?? row.next_due_date,
        Note: row.Note ?? row.note,
      };
    }

    // Medication
    if ("medication_name" in row || "Medication Name" in row) {
      return {
        ...row,
        id, // <-- keep id
        Veterinarian: row.Veterinarian ?? row.veterinarian,
        "Medication Name": row["Medication Name"] ?? row.medication_name,
        Dosage: row.Dosage ?? row.dosage,
        "Start Date": row["Start Date"] ?? row.start_date,
        "Currently Taking":
          row["Currently Taking"] ??
          (typeof row.currently_taking === "boolean"
            ? row.currently_taking
              ? "Yes"
              : "No"
            : row.currently_taking),
      };
    }

    // Allergy
    if ("allergen" in row || "Allergen" in row) {
      return {
        ...row,
        id, // keep id for delete/edit
        Allergen: row.Allergen ?? row.allergen,
        Reaction: row.Reaction ?? row.reaction,
        Severity: row.Severity ?? cap(row.severity), // UI shows "Mild/Moderate/Severe"
        Note: row.Note ?? row.note,
      };
    }

    // Past Illness History
    // Check if this is a past illness record - check for illness field OR if formType is pastIllness
    if (formType === 'pastIllness' || "illness" in row || "Illness" in row || "condition_name" in row || "Condition Name" in row) {
      // Extract final_diagnosis from multiple sources with priority
      const finalDiagnosis =
        row.final_diagnosis ??
        row.final_diag ??
        row.appointment?.final_diag ??
        row.visit?.final_diag ??
        null;

      // Extract illness name from multiple possible sources
      // If illness is null, try to extract from final_diagnosis as fallback
      const illnessName =
        row.illness ??
        row["Illness Name"] ??
        row.illness_name ??
        row.Illness ??
        row.condition_name ??
        row["Condition Name"] ??
        (finalDiagnosis ? finalDiagnosis.split('.')[0].split(',')[0].trim().substring(0, 50) : null); // Use first sentence/phrase of final diagnosis as fallback

      return {
        ...row,
        id,
        Veterinarian: row.Veterinarian ?? row.veterinarian,
        "Illness Name": illnessName,
        "Final Diagnosis": finalDiagnosis,
        "Diagnosis Date": row["Diagnosis Date"] ?? row.diagnosis_date, // optional: slice(0,10)
        "soap_note_id": row["soap_note_id"] ?? row.soap_note_id,
        "visit_id": row.visit_id ?? row.visit?.id ?? row.appointment?.visit_id ?? null,
        "visit_type": row.visit_type ?? row.visit?.type ?? (row.outpatient_visit_id ? "outpatient" : row.inpatient_visit_id ? "inpatient" : null),
      };
    }

    // Surgical History
    if ("surgery_name" in row || "Surgery Name" in row) {
      return {
        ...row,
        id,
        Veterinarian: row.Veterinarian ?? row.veterinarian,
        "Surgery Name": row["Surgery Name"] ?? row.surgery_name,
        Date: row.Date ?? (row.date ? String(row.date).slice(0, 10) : row.date),
        Note: row.Note ?? row.note,
      };
    }

    // Dental Records
    if ("dental_procedure" in row || "Dental Procedure" in row) {
      return {
        ...row,
        id,
        Veterinarian: row.Veterinarian ?? row.veterinarian,
        "Dental Procedure": row["Dental Procedure"] ?? row.dental_procedure,
        Date: row.Date ?? (row.date ? String(row.date).slice(0, 10) : row.date),
        Note: row.Note ?? row.note,
      };
    }

    // Deworming & Anti-Flea History
    if ("type" in row || "Type" in row) {
      const typeValue = row.type ?? row.Type ?? "";
      const displayType = typeValue === "deworming" ? "Deworming" : typeValue === "anti_flea" ? "Anti-Flea" : typeValue;

      // Format date properly - handle ISO datetime strings
      let formattedDate = "";
      if (row.Date) {
        formattedDate = String(row.Date).slice(0, 10);
      } else if (row.date) {
        const dateStr = String(row.date);
        // Handle ISO datetime format: "2025-11-23T21:00:00.0000000Z"
        if (dateStr.includes('T')) {
          formattedDate = dateStr.split('T')[0];
        } else {
          formattedDate = dateStr.slice(0, 10);
        }
      }

      // Format next due date properly
      let formattedNextDueDate = "";
      if (row["Next Due Date"]) {
        const nextDateStr = String(row["Next Due Date"]);
        if (nextDateStr.includes('T')) {
          formattedNextDueDate = nextDateStr.split('T')[0];
        } else {
          formattedNextDueDate = nextDateStr.slice(0, 10);
        }
      } else if (row.next_due_date) {
        const nextDateStr = String(row.next_due_date);
        if (nextDateStr.includes('T')) {
          formattedNextDueDate = nextDateStr.split('T')[0];
        } else {
          formattedNextDueDate = nextDateStr.slice(0, 10);
        }
      }

      return {
        ...row,
        id,
        Veterinarian: row.Veterinarian ?? row.veterinarian,
        Type: displayType,
        type: typeValue,
        Date: formattedDate || "-",
        "Next Due Date": formattedNextDueDate || "-",
        Note: row.Note ?? row.note ?? "-",
      };
    }

    // Fallback mapping for APIs that return a common shape like { id, title, date }
    if (('title' in row) || ('date' in row)) {
      // Vaccination entries often come as { id, title, date }
      if (formType === 'vaccination') {
        return {
          ...row,
          id,
          Veterinarian: row.Veterinarian ?? row.veterinarian,
          'Vaccine Name': row['Vaccine Name'] ?? row.title,
          'Date Administered': row['Date Administered'] ?? row.date ?? row.date_administered,
          'Next Due Date': row['Next Due Date'] ?? row.next_due_date,
          Note: row.Note ?? row.note ?? row.note_text ?? null,
        };
      }

      if (formType === 'medication') {
        return {
          ...row,
          id,
          Veterinarian: row.Veterinarian ?? row.veterinarian,
          'Medication Name': row['Medication Name'] ?? row.title,
          Dosage: row.Dosage ?? row.dosage,
          'Start Date': row['Start Date'] ?? row.date ?? row.start_date,
          'Currently Taking':
            row['Currently Taking'] ??
            (typeof row.currently_taking === 'boolean'
              ? row.currently_taking
                ? 'Yes'
                : 'No'
              : row.currently_taking),
        };
      }

      if (formType === 'surgery') {
        return {
          ...row,
          id,
          Veterinarian: row.Veterinarian ?? row.veterinarian,
          'Surgery Name': row['Surgery Name'] ?? row.title,
          Date: row.Date ?? (row.date ? String(row.date).slice(0, 10) : row.date),
          Note: row.Note ?? row.note ?? null,
        };
      }

      if (formType === 'dental') {
        return {
          ...row,
          id,
          Veterinarian: row.Veterinarian ?? row.veterinarian,
          'Dental Procedure': row['Dental Procedure'] ?? row.title,
          Date: row.Date ?? (row.date ? String(row.date).slice(0, 10) : row.date),
          Note: row.Note ?? row.note ?? null,
        };
      }

      if (formType === 'pastIllness') {
        // Extract final_diagnosis from multiple sources with priority
        const finalDiagnosis =
          row.final_diagnosis ??
          row.final_diag ??
          row.appointment?.final_diag ??
          row.visit?.final_diag ??
          null;

        return {
          ...row,
          id,
          Veterinarian: row.Veterinarian ?? row.veterinarian,
          'Illness Name': row.illness ?? row['Illness Name'] ?? row.illness_name ?? row.Illness ?? row.title ?? row.name ?? null,
          'Final Diagnosis': finalDiagnosis,
          'Diagnosis Date': row['Diagnosis Date'] ?? row.date ?? row.diagnosis_date,
          'soap_note_id': row['soap_note_id'] ?? row.soap_note_id,
          'visit_id': row.visit_id ?? row.visit?.id ?? row.appointment?.visit_id ?? null,
          'visit_type': row.visit_type ?? row.visit?.type ?? (row.outpatient_visit_id ? "outpatient" : row.inpatient_visit_id ? "inpatient" : null),
        };
      }

      if (formType === 'allergy') {
        return {
          ...row,
          id,
          Allergen: row.Allergen ?? row.title ?? row.allergen,
          Reaction: row.Reaction ?? row.reaction,
          Severity: row.Severity ?? cap(row.severity) ?? null,
          Note: row.Note ?? row.note ?? null,
        };
      }

      if (formType === 'dewormingAntiFlea') {
        const typeValue = row.type ?? row.Type ?? "";
        const displayType = typeValue === "deworming" ? "Deworming" : typeValue === "anti_flea" ? "Anti-Flea" : typeValue;

        // Format date properly - handle ISO datetime strings
        let formattedDate = "";
        if (row.Date) {
          formattedDate = String(row.Date).slice(0, 10);
        } else if (row.date) {
          const dateStr = String(row.date);
          // Handle ISO datetime format: "2025-11-23T21:00:00.0000000Z"
          if (dateStr.includes('T')) {
            formattedDate = dateStr.split('T')[0];
          } else {
            formattedDate = dateStr.slice(0, 10);
          }
        }

        // Format next due date properly
        let formattedNextDueDate = "";
        if (row['Next Due Date']) {
          const nextDateStr = String(row['Next Due Date']);
          if (nextDateStr.includes('T')) {
            formattedNextDueDate = nextDateStr.split('T')[0];
          } else {
            formattedNextDueDate = nextDateStr.slice(0, 10);
          }
        } else if (row.next_due_date) {
          const nextDateStr = String(row.next_due_date);
          if (nextDateStr.includes('T')) {
            formattedNextDueDate = nextDateStr.split('T')[0];
          } else {
            formattedNextDueDate = nextDateStr.slice(0, 10);
          }
        }

        return {
          ...row,
          id,
          Veterinarian: row.Veterinarian ?? row.veterinarian,
          Type: displayType,
          type: typeValue,
          Date: formattedDate || "-",
          'Next Due Date': formattedNextDueDate || "-",
          Note: row.Note ?? row.note ?? "-",
        };
      }
    }

    return { ...row, id };
  };

  // Fetch Data
  const fetchData = async () => {
    try {
      // Always fetch from API endpoint to ensure we have the latest data
      // The shared data is used as initial data, but we always refetch after mutations
      // Add per_page=100 to ensure we get all records and avoid backend default pagination hiding older records
      const res = await api.get(fetchUrl, {
        params: {
          ...(petData?.id ? { pet_id: petData.id } : {}),
          per_page: 100,
          limit: 100,
          simple: true
        }
      });
      const json = res.data;

      let rows = (json.data ?? json ?? []).map(normalizeRowForDisplay);

      // If this is 'pastIllness' view, we also want to include completed/checkout inpatient visits
      // because they essentially represent a medical history record.
      if (formType === 'pastIllness' && petData?.id) {
        try {
          // Fetch inpatient visits for this pet
          // We use the search endpoint or a specific pet-visits endpoint
          const inpatientRes = await api.get(`/api/v1/clinic-appointments/search-inpatient-visits`, {
            params: {
              pet_id: petData.id,
              status: 'checkout,completed,closed' // Only completed ones relevant for history
            }
          });

          if (inpatientRes.data?.data?.inpatient_visits) {
            const inpatientVisits = inpatientRes.data.data.inpatient_visits.map(visit => {
              // Map inpatient visit to expected "Past Illness" shape
              const finalDiag = visit.final_diag || visit.final_diagnosis || visit.reason || "Inpatient Visit";

              // Construct Plan content from Plan + Services/Procedures
              let planContent = visit.plan || "";

              // Add services/procedures if available
              const services = visit.services || visit.procedures || [];
              if (services.length > 0) {
                const serviceNames = services.map(s => s.name || s.service_name || s.title).filter(Boolean).join(", ");
                if (serviceNames) {
                  planContent = planContent ? `${planContent}\n\nProcedures: ${serviceNames}` : `Procedures: ${serviceNames}`;
                }
              }

              return {
                id: `inpatient-${visit.id}`, // specific ID prefix to avoid collision
                'Illness Name': "Inpatient: " + (visit.reason || "Visit"),
                'Final Diagnosis': finalDiag,
                'Diagnosis Date': visit.date || visit.visit_date?.split(' ')[0],
                'Veterinarian': visit.vet ? `${visit.vet.first_name || ''} ${visit.vet.last_name || ''}` : null,
                'Plan': planContent, // Add explicit Plan field
                type: 'inpatient_history',
                originalData: visit
              };
            });

            // Merge with existing rows
            rows = [...rows, ...inpatientVisits];
            // Sort by date descending
            rows.sort((a, b) => new Date(b['Diagnosis Date']) - new Date(a['Diagnosis Date']));
          }
        } catch (err) {
          console.warn("Failed to fetch inpatient history for medical records", err);
          // Don't block main data if this fails
        }
      }

      // Debug: Log raw API response for past illness
      if (formType === 'pastIllness') {
        if (json.data && json.data.length > 0) {
        }
      }

      // Debug: Log normalized rows for past illness
      if (formType === 'pastIllness') {
      }

      setData(rows);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Fallback to shared data if API fetch fails and we have shared data
      if (useSharedData && medicalHistoryData) {
        const rows = medicalHistoryData.map(normalizeRowForDisplay);
        setData(rows);
      }
    }
  };

  const saveData = async () => {
    const isAdd = formMode === "add";
    const url = isAdd ? fetchUrl : `${fetchUrl}/${formData.id}`;
    const method = isAdd ? "post" : "put";

    try {
      // Ensure payload contains API-expected snake_case keys even if the formData
      // uses UI-friendly keys (e.g., 'Vaccine Name'). This covers the case where
      // the user opens edit and doesn't change a field, so the corresponding
      // snake_case key may be missing.
      const payload = { ...formData, pet_id: petData?.id };

      // Normalize by formType
      if (formType === 'vaccination') {
        payload.vaccine_name = payload.vaccine_name ?? payload['Vaccine Name'] ?? payload.title ?? payload.name;
        payload.date_administered = payload.date_administered ?? payload['Date Administered'] ?? payload.date ?? payload.date_administered;
        payload.next_due_date = payload.next_due_date ?? payload['Next Due Date'] ?? payload.next_due_date;
        payload.veterinarian = payload.veterinarian ?? payload.Veterinarian ?? null;
        payload.note = payload.note ?? payload.Note ?? null;
      }
      if (formType === 'medication') {
        payload.medication_name = payload.medication_name ?? payload['Medication Name'] ?? payload.title ?? payload.name;
        payload.dosage = payload.dosage ?? payload.Dosage ?? null;
        payload.start_date = payload.start_date ?? payload['Start Date'] ?? payload.date ?? null;
        // Ensure currently_taking is always a boolean
        const currentlyTakingValue = payload.currently_taking ?? payload['Currently Taking'] ?? payload.CurrentlyTaking;
        if (typeof currentlyTakingValue === 'boolean') {
          payload.currently_taking = currentlyTakingValue;
        } else if (typeof currentlyTakingValue === 'string') {
          payload.currently_taking = currentlyTakingValue.toLowerCase() === 'yes' || currentlyTakingValue === 'true';
        } else {
          payload.currently_taking = false; // Default to false if not provided
        }
        payload.veterinarian = payload.veterinarian ?? payload.Veterinarian ?? null;
      }
      if (formType === 'surgery') {
        payload.surgery_name = payload.surgery_name ?? payload['Surgery Name'] ?? payload.title ?? payload.name;
        payload.date = payload.date ?? payload.Date ?? payload.date ?? null;
        payload.note = payload.note ?? payload.Note ?? null;
        payload.veterinarian = payload.veterinarian ?? payload.Veterinarian ?? null;
      }
      if (formType === 'dental') {
        payload.dental_procedure = payload.dental_procedure ?? payload['Dental Procedure'] ?? payload.title ?? payload.name;
        payload.date = payload.date ?? payload.Date ?? payload.date ?? null;
        payload.note = payload.note ?? payload.Note ?? null;
        payload.veterinarian = payload.veterinarian ?? payload.Veterinarian ?? null;
      }
      if (formType === 'pastIllness') {
        payload.diagnosis_date = payload.diagnosis_date ?? payload['Diagnosis Date'] ?? payload.date ?? null;
        payload.veterinarian = payload.veterinarian ?? payload.Veterinarian ?? null;
        payload.soap_note_id = payload.soap_note_id ?? payload['soap_note_id'] ?? null;
      }
      if (formType === 'allergy') {
        payload.allergen = payload.allergen ?? payload['Allergen'] ?? payload.title ?? payload.name;
        payload.reaction = payload.reaction ?? payload.Reaction ?? null;
        payload.severity = payload.severity ?? (payload.Severity ?? null);
        payload.note = payload.note ?? payload.Note ?? null;
      }
      if (formType === 'dewormingAntiFlea') {
        payload.type = payload.type ?? payload.Type ?? null;
        payload.date = payload.date ?? payload.Date ?? null;
        payload.next_due_date = payload.next_due_date ?? payload['Next Due Date'] ?? null;
        payload.veterinarian = payload.veterinarian ?? payload.Veterinarian ?? null;
        payload.note = payload.note ?? payload.Note ?? null;
      }

      // accept 2xx and 204 (no content)
      const res = await api({
        url,
        method,
        data: payload,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 204,
      });

      // if server returned no body (204), fall back to what we sent
      const saved = res.status === 204 ? payload : (res.data?.data ?? res.data);

      const normalized = normalizeRowForDisplay(saved);

      // If using shared data, refetch from API to ensure we have the latest data
      // Otherwise, update local state optimistically
      if (useSharedData) {
        // Refetch from API to get the complete, fresh data
        await fetchData();
      } else {
        setData((prev) =>
          isAdd
            ? [...prev, normalized]
            : prev.map((item, idx) =>
              idx === formData._index ? normalized : item
            )
        );
      }

      // Refresh medical history summary if callback provided
      if (onMedicalHistoryChange) {
        onMedicalHistoryChange();
      }

      // close form + reset
      setIsFormOpen(false);
      setFormData({});
    } catch (err) {
      console.error(
        "Error saving data:",
        err.response?.status,
        err.response?.data || err.message
      );
      // If the server returned validation errors, rethrow them so the form can display messages
      if (err.response?.data) {
        throw err.response.data
      }
      throw err
    }
  };

  const handleAdd = () => {
    setFormMode("add");
    setFormData({});
    setIsFormOpen(true);
  };

  const handleEdit = (row, index) => {
    setFormMode("edit");
    setFormData({ ...row, _index: index });
    setIsFormOpen(true);
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    setFormData({});
  };

  // Delete functions
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRow(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!row?.id) throw new Error("Missing row id");
      const base =
        formType === "vaccination"
          ? "/api/v1/pet-health/vaccinations-id"
          : formType === "medication"
            ? "/api/v1/pet-health/medications-id"
            : formType === "allergy"
              ? "/api/v1/pet-health/allergies-id"
              : formType === "pastIllness"
                ? "/api/v1/pet-health/past-illness-history-id"
                : formType === "surgery"
                  ? "/api/v1/pet-health/surgeries-id"
                  : formType === "dental"
                    ? "/api/v1/pet-health/dental-records-id"
                    : formType === "dewormingAntiFlea"
                      ? "/api/v1/pet-health/deworming-anti-flea-history-id"
                      : null;

      if (!base) throw new Error("Unknown formType for delete");

      const url = `${base}/${row.id}`;
      await api.delete(url);

      // If using shared data, refetch from API to ensure we have the latest data
      // Otherwise, update local state optimistically
      if (useSharedData) {
        // Refetch from API to get the complete, fresh data
        await fetchData();
      } else {
        // Update the local state immediately by removing the deleted item
        setData((prevData) => prevData.filter((item) => getId(item) !== row.id));

        // Reset pagination to first page if current page becomes empty
        const remainingItems = data.length - 1;
        const maxPage = Math.ceil(remainingItems / rowsPerPage);
        if (currentPage > maxPage && maxPage > 0) {
          setCurrentPage(maxPage);
        }
      }

      // Refresh medical history summary if callback provided
      if (onMedicalHistoryChange) {
        onMedicalHistoryChange();
      }
    } catch (err) {
      console.error("Error deleting:", err);
      // Optionally show an error message to user
      alert("Failed to delete record. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setRow(null);
      setIndex(undefined);
    }
  };

  const deleteData = (row, index) => {
    setRow(row);
    setIndex(index);
    setShowDeleteModal(true);
  };

  // Pagination
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const currentData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  useEffect(() => {
    // On initial load, use shared data if available for faster rendering
    // Otherwise fetch from API
    if (useSharedData && medicalHistoryData && data.length === 0) {
      const rows = medicalHistoryData.map(normalizeRowForDisplay);
      setData(rows);
      // Also fetch from API in background to ensure we have latest data
      fetchData();
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSharedData, medicalHistoryData, petData?.id]);

  // Choose correct form
  const renderForm = () => {
    switch (formType) {
      case "vaccination":
        return (
          <VaccinationForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      case "medication":
        return (
          <MedicationForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      case "pastIllness":
        return (
          <PastIllnessForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      case "allergies":
        return (
          <AllergyForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      case "surgery":
        return (
          <SurgicalHistoryForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      case "dental":
        return (
          <DentalRecordsForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      case "dewormingAntiFlea":
        return (
          <DewormingAntiFleaForm
            formData={formData}
            setFormData={setFormData}
            onSave={saveData}
            onCancel={handleCancel}
            formMode={formMode}
            petData={petData}
            userTypeColor={userTypeColor}
          />
        );
      default:
        // Generic placeholder form
        return (
          <div
            style={{ padding: "20px", background: "#fff", borderRadius: "8px" }}
          >
            <p>
              No custom form yet for <strong>{formType}</strong>.
            </p>
            <button onClick={handleCancel}>Back</button>
          </div>
        );
    }
  };

  return (
    <div
      style={{ marginBottom: "40px" }}
      dir={i18n?.dir?.()}
      lang={i18n?.language}
      className={isRTL ? "medical-record-view-rtl" : undefined}
    >

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {icon && (
            <img
              src={icon}
              alt="icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {title}
        </h2>

        {!isFormOpen && (
          <button
            onClick={handleAdd}
            style={{
              background: userTypeColor,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontFamily: '"Red Hat Display", sans-serif',
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              const hoverColor = userTypeColor === "#8548A8" ? "#7A4397" :
                userTypeColor === "#00D4B3" ? "#00C4A7" :
                  userTypeColor === "#D65745" ? "#C7493A" :
                    userTypeColor === "#2A8AD2" ? "#2596BE" : userTypeColor;
              e.target.style.background = hoverColor;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = userTypeColor;
            }}
          >
            {t("guardianDash.recordViews.addButton", { item: title })}
          </button>
        )}
      </div>

      {/* Form or Table with Animation */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {/* Table */}
        <div
          style={{
            opacity: isFormOpen ? 0 : 1,
            transform: isFormOpen ? "translateY(-20px)" : "translateY(0)",
            transition: "opacity 0.4s ease, transform 0.4s ease",
            position: isFormOpen ? "absolute" : "relative",
            width: "100%",
            pointerEvents: isFormOpen ? "none" : "all",
            zIndex: isFormOpen ? 1 : 2,
          }}
        >
          {/* Card wrapper for table */}
          <div style={{
            background: "#fff",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #E5E7EB"
          }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  {columns.map((col, i) => {
                    const label = typeof col === 'string' ? col : col.label;
                    return (
                      <th
                        key={i}
                        style={{
                          textAlign: isRTL ? "right" : "left",
                          padding: "16px 20px",
                          backgroundColor: "#F9FAFB",
                          borderBottom: "2px solid #E5E7EB",
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#374151",
                          fontFamily: '"Red Hat Display", sans-serif'
                        }}
                      >
                        {label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        padding: "40px 20px",
                        textAlign: "center",
                        color: "#6B7280",
                        fontSize: "14px",
                        fontFamily: '"Red Hat Display", sans-serif'
                      }}
                    >
                      {t("guardianDash.recordViews.empty", { item: title })}
                    </td>
                  </tr>
                )}

                {/* Render actual rows for the current page */}
                {currentData.map((row, ri) => (
                  <tr
                    key={ri}
                    style={{
                      borderBottom: "1px solid #E5E7EB",
                      transition: "background-color 0.2s",
                      cursor: showActions && !(formType === 'pastIllness' && row.soap_note_id) ? "pointer" : "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#F9FAFB";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }}
                    onClick={() => {
                      // Allow row click to edit if actions are enabled
                      if (showActions && !(formType === 'pastIllness' && row.soap_note_id)) {
                        handleEdit(row, (currentPage - 1) * rowsPerPage + ri);
                      }
                    }}
                  >
                    {columns.map((col, ci) => {
                      const field = typeof col === 'string' ? col : col.field;
                      const label = typeof col === 'string' ? col : col.label;
                      // Prefer explicit field, otherwise try label as key
                      const value = field && field in row ? row[field] : row[label];

                      // Handle Actions column
                      if (field === 'Actions' || label === 'Actions' || label?.includes('Actions') || field?.includes('Actions')) {
                        const hasSOAPNote = row.soap_note_id || row['soap_note_id'];
                        return (
                          <td
                            key={ci}
                            style={{
                              padding: "16px 20px",
                              borderBottom: "1px solid #E5E7EB",
                              fontSize: "14px",
                              color: "#111827",
                              fontFamily: '"Red Hat Display", sans-serif',
                              textAlign: "center",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {hasSOAPNote ? (
                              <button
                                onClick={() => {
                                  setSelectedSOAPRow(row);
                                  setShowSOAPModal(true);
                                }}
                                style={{
                                  background: userTypeColor,
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "6px 16px",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  fontFamily: '"Red Hat Display", sans-serif',
                                }}
                                onMouseEnter={(e) => {
                                  const hoverColor = userTypeColor === "#8548A8" ? "#7A4397" :
                                    userTypeColor === "#00D4B3" ? "#00C4A7" :
                                      userTypeColor === "#D65745" ? "#C7493A" :
                                        userTypeColor === "#2A8AD2" ? "#2596BE" : userTypeColor;
                                  e.target.style.background = hoverColor;
                                  e.target.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = userTypeColor;
                                  e.target.style.transform = "scale(1)";
                                }}
                              >
                                {t("petProfile.medicalRecords.actions.soap")}
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      }

                      // Handle Final Diagnosis column with truncation
                      if (field === 'Final Diagnosis' || label === 'Final Diagnosis') {
                        const diagnosisText = value || "";
                        const maxLength = 50;
                        const truncated = diagnosisText.length > maxLength
                          ? diagnosisText.substring(0, maxLength) + "..."
                          : diagnosisText;

                        return (
                          <td
                            key={ci}
                            style={{
                              padding: "16px 20px",
                              borderBottom: "1px solid #E5E7EB",
                              fontSize: "14px",
                              color: "#111827",
                              fontFamily: '"Red Hat Display", sans-serif',
                              textAlign: isRTL ? "right" : "left",
                              maxWidth: "300px",
                            }}
                            title={diagnosisText || undefined}
                          >
                            {truncated || "-"}
                          </td>
                        );
                      }

                      // Default cell rendering
                      return (
                        <td
                          key={ci}
                          style={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #E5E7EB",
                            fontSize: "14px",
                            color: "#111827",
                            fontFamily: '"Red Hat Display", sans-serif',
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {value || "-"}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* Add placeholder rows so the table always shows at least MIN_VISIBLE_ROWS rows */}
                {Array.from({ length: Math.max(0, MIN_VISIBLE_ROWS - currentData.length) }).map((_, pi) => (
                  <tr key={`placeholder-${pi}`} style={{ borderBottom: "1px solid #E5E7EB" }}>
                    {columns.map((col, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #E5E7EB",
                          color: "transparent",
                          fontSize: "14px",
                        }}
                      >
                        -
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                marginTop: "20px",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#F9FAFB",
                borderRadius: "0 0 12px 12px",
                borderTop: "1px solid #E5E7EB",
              }}
            >
              {/* Left: Showing range */}
              <div style={{ fontSize: "14px", color: "#6B7280", fontFamily: '"Red Hat Display", sans-serif' }}>
                Showing {(currentPage - 1) * rowsPerPage + 1}–
                {Math.min(currentPage * rowsPerPage, data.length)} of{" "}
                {data.length}
              </div>

              {/* Right: Prev/Next buttons with icons */}
              <div
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    background: currentPage === 1 ? "#F3F4F6" : "#fff",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === 1 ? 0.5 : 1,
                    transition: "all 0.2s",
                    fontFamily: '"Red Hat Display", sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.target.style.borderColor = userTypeColor;
                      e.target.style.color = userTypeColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.target.style.borderColor = "#D1D5DB";
                      e.target.style.color = "#374151";
                    }
                  }}
                >
                  <img
                    src="/icons/prev.png"
                    alt="Previous"
                    width={16}
                    height={16}
                  />
                </button>
                <span style={{ fontSize: "14px", color: "#374151", fontFamily: '"Red Hat Display", sans-serif', fontWeight: 500 }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  style={{
                    background: currentPage === totalPages ? "#F3F4F6" : "#fff",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    transition: "all 0.2s",
                    fontFamily: '"Red Hat Display", sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.target.style.borderColor = userTypeColor;
                      e.target.style.color = userTypeColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== totalPages) {
                      e.target.style.borderColor = "#D1D5DB";
                      e.target.style.color = "#374151";
                    }
                  }}
                >
                  <img
                    src="/icons/next.png"
                    alt="Next"
                    width={16}
                    height={16}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <div
          style={{
            opacity: isFormOpen ? 1 : 0,
            transform: isFormOpen ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.4s ease, transform 0.4s ease",
            position: isFormOpen ? "relative" : "absolute",
            width: "100%",
            pointerEvents: isFormOpen ? "all" : "none",
            zIndex: isFormOpen ? 2 : 1,
          }}
        >
          {renderForm()}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemToDelete="Record"
      />

      {/* SOAP Details Modal */}
      {formType === 'pastIllness' && selectedSOAPRow && (
        <SOAPDetailsModal
          isOpen={showSOAPModal}
          onClose={() => {
            setShowSOAPModal(false);
            setSelectedSOAPRow(null);
          }}
          visitId={selectedSOAPRow.visit_id}
          visitType={selectedSOAPRow.visit_type}
        />
      )}
    </div>
  );
};

export default MedicalRecordView;
